import { supabase } from '@/lib/supabase';
import { apiService } from './apiService';
import { TempOnboardingService } from './tempOnboardingService';
import { ClinicSelectionError, clinicSelectionPayload, clinicCompletionError, ensureClinicCompletionSupport } from './onboardingClinicService';

interface CompletionResponse {
  success: boolean;
  data?: { assignment?: { clinic_id: number; replayed?: boolean } };
  code?: string;
}

export async function completeAuthenticatedOnboarding(provider: 'google' | 'email', expectedUserId: string) {
  const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
  if (sessionError || !expectedUserId || !sessionData.session?.access_token ||
      sessionData.session.user.id !== expectedUserId ||
      sessionData.session?.user.app_metadata?.provider !== provider) {
    throw new Error('Onboarding session changed');
  }
  let draft;
  try { draft = await TempOnboardingService.getDataStrict(); }
  catch { throw new ClinicSelectionError('ONBOARDING_INCOMPLETE'); }
  const onboardingData = {
    name: draft.name, interests: draft.interests, gender: draft.gender,
    support_recipient: draft.supportRecipient, sponsorship_preference: draft.sponsorshipPreference,
    addiction_types: draft.addictionTypes, addiction_frequency: draft.addictionFrequency,
    app_goal: draft.appGoal, ...clinicSelectionPayload(draft),
  };
  await ensureClinicCompletionSupport(provider === 'email' ? 'email_session' : undefined);
  // getSession already restores/refreshes an expired session. Do not make a
  // second, forced refresh a prerequisite for sending otherwise valid answers:
  // a refresh timeout must not block signup while the access token still works.
  // apiService retains its expiry check and single refresh+retry on HTTP 401.
  const result = await apiService.post<CompletionResponse>(
    provider === 'google' ? '/api/auth/google/complete-profile' : '/api/auth/email/complete-profile',
    { onboardingData, expected_user_id: expectedUserId },
  );
  if (!result.success || result.data?.success !== true) {
    throw clinicCompletionError(result.code ?? result.data?.code, result.status) ??
      new Error('Onboarding completion unavailable');
  }
  const assignment = result.data.data?.assignment;
  if (!assignment || !Number.isSafeInteger(assignment.clinic_id) || assignment.clinic_id < 1) {
    throw new Error('Invalid onboarding completion response');
  }
  const { data: currentSession, error: currentError } = await supabase.auth.getSession();
  if (currentError || currentSession.session?.user.id !== expectedUserId) {
    // The original account may have committed, but a different account must
    // not have its current draft cleared or receive signup navigation/events.
    throw new Error('Onboarding session changed');
  }
  return assignment;
}
